import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import dayjs from 'dayjs';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { DATE_FORMAT } from 'src/common/const';
import { request } from 'src/common/request';
import { getCurrentSpaceId } from 'src/hooks/space/get-space';
import { useIsProSpace } from 'src/hooks/space/use-is-pro-space';
import { useIsAiPlan, useSpaceCapacity } from 'src/services/capacity';
import type { AsyncReturnType } from 'type-fest';

type AiDetailItem = AsyncReturnType<typeof request.infra.getSpaceAiDetail>[number];

/** ai配额历史 */
export const FlowusAIHistory: FC = () => {
  const { t } = useAppTranslation();
  const { aiRestUsage, usage, baseModel, pdf } = useSpaceCapacity();
  const scrollRef = useRef<HTMLDivElement>(null);
  const isAiPlan = useIsAiPlan();
  const isProSpace = useIsProSpace();

  /** 积分邀请 */
  const [integralList, setIntegralList] = useState<
    AsyncReturnType<typeof request.infra.getSpaceAiDetail>
  >([]);

  useEffect(() => {
    void loadIntegralList(1);
  }, []);

  const loadIntegralList = async (page: number) => {
    const spaceId = getCurrentSpaceId();
    const res = await request.infra.getSpaceAiDetail(spaceId, page, 20);
    setIntegralList(res);
  };

  const monthUsed = baseModel.monthUsed + pdf.monthUsed;

  const baseModelRest = isAiPlan
    ? baseModel.monthLimit - baseModel.monthUsed
    : baseModel.dayLimit - baseModel.todayUsed;

  const pdfRest = isAiPlan ? pdf.dayLimit - pdf.todayUsed : pdf.monthLimit - pdf.monthUsed;

  return (
    <div className="py-[5px] max-h-[60vh] min-w-[500px] flex flex-col">
      <div className="flex items-center justify-between h-[50px] px-7 flex-shrink-0">
        <div className="text-h4">
          {t('common.product_ai_title')} {t('views.settings.ai_history.usage_details')}
        </div>
        <p className="text-t2 text-grey3">
          {t('views.settings.ai_history.used_times', {
            used: __BUILD_IN__ ? monthUsed : usage,
            remaining: __BUILD_IN__
              ? baseModelRest + pdfRest
              : isProSpace
              ? t('views.settings.ai_history.unlimited')
              : aiRestUsage,
          })}
        </p>
      </div>
      <div className="bg-grey6 h-px my-1 w-full flex-shrink-0" />
      <div ref={scrollRef} className="overflow-y-auto h-full">
        <InfiniteScroll
          className="flex flex-col px-7 space-y-2"
          pageStart={1}
          initialLoad={false}
          useWindow={false}
          loadMore={loadIntegralList}
          getScrollParent={() => scrollRef.current}
          // hasMore={integralList.total > integralList.list.length}
        >
          <div className="text-t2 h-10 flex items-center">
            <span className="w-40">{t('views.settings.ai_history.record_type')}</span>
            <span className="w-28">{t('views.settings.ai_history.request_count')}</span>
            <span className="w-28">{t('views.settings.ai_history.usage_amount')}</span>
            <span className="w-40">{t('views.settings.ai_history.effective_time')}</span>
            <span className="w-40">{t('views.settings.ai_history.record_time')}</span>
          </div>
          <div className="bg-grey6 h-px my-1 space-y-2" />

          {integralList.map((item: AiDetailItem) => (
            <div key={item.updatedAt} className="flex w-full justify-between items-center h-10">
              <div className="text-t2 w-40 flex flex-col">
                {item.title}
                {(item.endAt ?? 0) > 0 && (
                  <span className="text-t4 text-grey3 w-[140px]">
                    {t('common.valid_until', {
                      date: dayjs(item.endAt).format(DATE_FORMAT),
                    })}
                  </span>
                )}
              </div>
              <div className="text-t2 w-28">{item.count}</div>
              <div className="text-t2 w-28">
                {(item.usage ?? 0) > 0 && '+'}
                {item.usage}
              </div>
              {item.createdAt && (
                <div className="text-t2 w-40">
                  {dayjs(item.createdAt).format('YYYY/MM/DD HH:mm')}
                </div>
              )}
              {item.updatedAt && (
                <div className="text-t2 w-40">
                  {dayjs(item.updatedAt).format('YYYY/MM/DD HH:mm')}
                </div>
              )}
            </div>
          ))}
          {!integralList.length && (
            <div className="text-t2 text-grey4 mt-2">{t('common.no_records')}</div>
          )}
        </InfiniteScroll>
      </div>
    </div>
  );
};
